import React from 'react';
import { Helmet } from '@lib/helmet-shim';
import { getRobotsMeta, SEO_CONFIG, SUPPORTED_LANGUAGES, siteConfig } from '../config';

interface SeoHeadProps {
  title?: string;
  description?: string;
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  url?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
  /** ISO 8601 date string for article:published_time */
  publishedTime?: string;
  /** ISO 8601 date string for article:modified_time */
  modifiedTime?: string;
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
  url,
  canonicalUrl,
  jsonLd,
  publishedTime,
  modifiedTime,
}) => {
  const robotsMeta = getRobotsMeta(pageSlug);
  const fullTitle = title
    ? `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}`
    : undefined;
  // Always prefer explicitly-passed canonicalUrl or url; never fall back to window.location
  const canonical = canonicalUrl || url || siteConfig.siteUrl;

  // Resolve absolute image URL
  const imageUrl = image.startsWith('http') ? image : `${siteConfig.siteUrl}${image}`;

  // Build hreflang alternate links based on supported languages
  // Strip trailing slash for consistent path handling
  const canonicalPath = canonical.replace(/\/$/, '');
  const isHomepage = canonicalPath === siteConfig.siteUrl.replace(/\/$/, '');

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternate links — signals multilingual content to Google */}
      {SUPPORTED_LANGUAGES.map(({ hreflang }) => {
        const langUrl = hreflang === 'en'
          ? canonical
          : isHomepage
            ? `${siteConfig.siteUrl}/${hreflang}`
            : `${canonicalPath}?hl=${hreflang}`;
        return <link key={hreflang} rel="alternate" hrefLang={hreflang} href={langUrl} />;
      })}
      {/* x-default always points to the canonical (English) URL */}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={fullTitle ?? siteConfig.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      <meta name="twitter:site" content={SEO_CONFIG.TWITTER_HANDLE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={fullTitle ?? siteConfig.siteName} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
